/**
 * 单词一体化学习流（Word Study Flow）
 * ------------------------------------------------------------
 * 设计要点（对应产品要求）：
 *   · **不再是「挑战 1 / 2 / 3」关卡**。学生看到的是「学 Unit 3 的 14 个词」这样一件事，
 *     系统在后台把 6 种训练方式穿插进去，学生只需要一直往前走。
 *   · **一个单词独立答对 3 次才算真正掌握**（MASTERY_GOAL）。
 *     答错不算数 → 重新排进队列后面再来一次；需要多次出现的词自然重复更多。
 *   · 每个单词都会先完整「认识」一遍（英文 / 中文 / 发音 / 教材例句 / 例句中文），
 *     再进入检测环节，避免一上来就被考。
 *   · 整轮结束后按本单元学过的词生成一小段语境应用任务（CONTEXT_TASK）。
 *
 * 这里只放纯逻辑（无 DOM），UI 在 pages/wordflow.js。
 * 复用 PracticeSession 做判分与落库，因此错题、掌握矩阵、XP 规则全部沿用，不另起一套。
 */
import { grade } from './graders/index.js';
import { shuffle, sample, tokenize, nowISO, uid } from '../utils/index.js';
import { makeQuestion } from '../core/models.js';

/** 一个单词需要独立答对几次才算掌握 */
export const MASTERY_GOAL = 3;
/** 极限保护：同一个词最多被考这么多次，防止极端情况（孩子一直不做对）卡死流程 */
const MAX_ATTEMPTS_PER_WORD = 15;

/**
 * 六种训练方式（学生不看到「第几关」，只看到动词短语）
 * preview 类型不判分，只用于「认识」。
 */
export const WORD_STEPS = [
  { key: 'meet', name: '认识单词', icon: '👀', kind: 'preview' },
  { key: 'listen-pick', name: '听音选词', icon: '👂', kind: 'quiz' },
  { key: 'en-zh', name: '看英文选中文', icon: '🇬🇧', kind: 'quiz' },
  { key: 'zh-en', name: '看中文选英文', icon: '🇨🇳', kind: 'quiz' },
  { key: 'listen-spell', name: '听音拼写', icon: '🎧', kind: 'quiz' },
  { key: 'write', name: '独立默写', icon: '✍️', kind: 'quiz' },
  { key: 'context', name: '例句填空', icon: '📖', kind: 'quiz' },
];

const STEP_ORDER = WORD_STEPS.map(s => s.key);
const stepRank = k => STEP_ORDER.indexOf(k);

/* ---------------------------- 出题（单步） ---------------------------- */

function distractors(word, pool, n, key) {
  const others = pool.filter(w => w.id !== word.id && w[key]);
  const picked = sample(others, n);
  if (picked.length < n) {
    const fallback = ['book', 'pen', 'cat', 'dog', 'red', 'run', 'school', 'apple'];
    for (const f of fallback) {
      if (picked.length >= n) break;
      if (String(word[key]).toLowerCase() === f) continue;
      picked.push({ [key]: f });
    }
  }
  return picked;
}

const meta = (word, step, extra) => ({
  textbookId: word.textbookId,
  unitId: word.unitId,
  refType: 'word',
  refId: word.id,
  origin: 'generated',
  status: 'confirmed',
  wordId: word.id,
  step,
  // level 沿用 1~6 的既有语义，掌握矩阵与「哪一级出错」统计不需要改
  level: LEVEL_OF_STEP[step] || 1,
  ...extra,
});

const LEVEL_OF_STEP = {
  'listen-pick': 1,
  'en-zh': 2,
  'zh-en': 3,
  'write': 4,
  'listen-spell': 5,
  'context': 6,
};

/**
 * 生成某单词的某一步题目
 * @returns {object|null} 题目对象；preview 步骤返回 null
 */
export function buildStepQuestion(word, stepKey, pool) {
  switch (stepKey) {
    case 'listen-pick':
      return makeQuestion(meta(word, stepKey, {
        type: 'listen',
        skill: 'W',
        prompt: '听一听，这是哪个单词？',
        audioUrl: word.audio || '',
        audio: word.en,
        options: shuffle([word.en, ...distractors(word, pool, 3, 'en').map(o => o.en)]),
        answer: [word.en],
        explain: `${word.en} ${word.phonetic || ''} = ${word.zh}`,
        hint: word.phonetic || '',
      }));

    case 'en-zh':
      return makeQuestion(meta(word, stepKey, {
        type: 'choice',
        skill: 'W',
        prompt: `“${word.en}” 是什么意思？`,
        audioUrl: word.audio || '',
        audio: word.en,
        options: shuffle([word.zh, ...distractors(word, pool, 3, 'zh').map(o => o.zh)]),
        answer: [word.zh],
        explain: `${word.en} ${word.phonetic || ''} = ${word.zh}`,
        hint: word.pos || '',
      }));

    case 'zh-en':
      return makeQuestion(meta(word, stepKey, {
        type: 'choice',
        skill: 'W',
        prompt: `“${word.zh}” 用英语怎么说？`,
        options: shuffle([word.en, ...distractors(word, pool, 3, 'en').map(o => o.en)]),
        answer: [word.en],
        explain: `${word.zh} = ${word.en} ${word.phonetic || ''}`,
        hint: `${word.en.slice(0, 1)}…`,
      }));

    case 'listen-spell':
      return makeQuestion(meta(word, stepKey, {
        type: 'spell',
        skill: 'W',
        prompt: '听发音，把单词拼出来',
        audioUrl: word.audio || '',
        audio: word.en,
        answer: [word.en],
        explain: `${word.en} ${word.phonetic || ''} = ${word.zh}`,
        hint: `共 ${word.en.replace(/[^a-zA-Z]/g, '').length} 个字母`,
      }));

    case 'write':
      return makeQuestion(meta(word, stepKey, {
        type: 'spell',
        skill: 'W',
        prompt: `看中文，默写单词：${word.zh}`,
        answer: [word.en],
        explain: `${word.en} ${word.phonetic || ''}`,
        hint: `第一个字母是 ${word.en[0]}`,
      }));

    case 'context': {
      // 有教材例句就挖空例句；没有就用短语；都没有就退回中文提示
      const src = word.sentence || word.phrase || '';
      if (src && /[a-zA-Z]/.test(src)) {
        const toks = tokenize(src);
        const idx = toks.findIndex(t => t.toLowerCase().replace(/[^a-z]/g, '') === word.en.toLowerCase().replace(/[^a-z]/g, ''));
        const blanks = idx >= 0 ? [idx] : [Math.max(0, toks.length - 2)];
        const answers = blanks.map(i => toks[i]);
        const shown = toks.map((t, i) => (blanks.includes(i) ? '____' : t)).join(' ')
          .replace(/\s+([.,!?;:])/g, '$1');
        return makeQuestion(meta(word, stepKey, {
          type: 'fill',
          skill: 'W',
          prompt: `把句子补充完整：${shown}`,
          audioUrl: '',
          audio: src,
          answer: answers,
          explain: `${src}\n${word.sentenceZh || word.phraseZh || ''}`,
          hint: `首字母 ${answers[0]?.[0] || word.en[0]}`,
        }));
      }
      return makeQuestion(meta(word, stepKey, {
        type: 'spell',
        skill: 'W',
        prompt: `用英文写出：${word.zh}`,
        answer: [word.en],
        explain: `${word.en} = ${word.zh}`,
        hint: '',
      }));
    }

    default:
      return null;
  }
}

/* ---------------------------- 学习流本身 ---------------------------- */

export class WordStudyFlow {
  /**
   * @param {object} cfg
   * @param {object[]} cfg.words         本单元单词
   * @param {string} cfg.unitId
   * @param {string} cfg.textbookId
   * @param {string} [cfg.title]
   * @param {Set<string>} [cfg.priority] 错题优先的 wordId
   * @param {number} [cfg.limit]         本次最多学多少个词（默认全部）
   * @param {string[]} [cfg.ids]         只学这些 wordId（自定义勾选）；与 limit 一起生效
   */
  constructor(cfg) {
    this.id = uid('wflow');
    this.unitId = cfg.unitId || '';
    this.textbookId = cfg.textbookId || '';
    this.title = cfg.title || '单词学习';
    // 自定义勾选：先按 ids 过滤，再进入后续排序逻辑
    const picked = Array.isArray(cfg.ids) && cfg.ids.length
      ? (cfg.words || []).filter(w => cfg.ids.includes(w.id))
      : (cfg.words || []);
    this.pool = picked.length ? picked : (cfg.words || []);

    let order = shuffle(this.pool);
    if (cfg.priority?.size) {
      order = [...order.filter(w => cfg.priority.has(w.id)), ...order.filter(w => !cfg.priority.has(w.id))];
    }
    const limit = Math.min(cfg.limit || order.length, order.length);
    this.words = order.slice(0, Math.max(1, limit));

    /** wordId → { word, seenSteps:Set, correct:number, wrong:number, independent:number } */
    this.stats = new Map();
    this.words.forEach(w => this.stats.set(w.id, {
      word: w, seenSteps: new Set(), correct: 0, wrong: 0, independent: 0, attempts: 0,
      roundAttempts: 0, retrySteps: [],
    }));

    this.queue = [];                 // 待做任务 { wordId, step }
    this.records = [];               // 每题结果（与 PracticeSession.records 同构）
    this.index = 0;
    this.startedAt = nowISO();
    this.questionStartedAt = Date.now();
    this._hintUsed = false;
    this.streak = 0;
    this._buildInitialQueue();
  }

  get total() { return this.queue.length + this.records.length; }
  get done() { return this.records.length; }
  get current() { return this.queue[0] || null; }
  get finished() { return this.queue.length === 0; }
  get progress() { return this.total ? this.done / this.total : 0; }

  /** 当前任务对应的题目 */
  currentQuestion() {
    const task = this.current;
    if (!task) return null;
    if (task.step === 'meet') {
      const st = this.stats.get(task.wordId);
      return { id: `meet-${task.wordId}`, type: '__meet', word: st.word, step: 'meet', refType: 'word', refId: task.wordId };
    }
    return buildStepQuestion(this.stats.get(task.wordId).word, task.step, this.pool);
  }

  /**
   * 初始队列：每个词先「认识」，然后进入第一轮检测。
   * 检测步骤按固定顺序推进，但同一单词的两次检测之间会插入别的词，
   * 形成「学习 → 稍后回想 → 再用」的间隔效果，而不是连着背 3 遍。
   */
  _buildInitialQueue() {
    // ① 认识阶段：每个词先看一张卡（不判分，只建立英文/中文/发音/例句的连接）
    this.queue = this.words.map(w => ({ wordId: w.id, step: 'meet' }));
    // ② 检测轮次从第 1 轮开始，由 _ensureWork() 在"认识"全部看完后自动排入
    this._round = 1;
    this._builtFirstRound = false;
  }

  /**
   * 每一轮的环节阶梯（每个词都会走完一整条，而不是只走同一题型）。
   *   R1 听音选词 → R2 看英文选中文 → R3 看中文选英文
   *   R4 听音拼写 → R5 独立默写 → R6 例句填空
   * 一个词"独立答对 3 次"分布在 3 种不同任务上，比同题型连做 3 次更能证明真的会了。
   */
  static LADDER = ['listen-pick', 'en-zh', 'zh-en', 'listen-spell', 'write', 'context'];

  /**
   * 构建某一轮的任务队列。规则：
   *   - 这一轮做第 r 个环节（r 从 1 开始）
   *   - 上一轮该词答错的，本轮仍用**同一个环节**再给它一次机会（retry）
   *   - 同一个词在一轮里最多出现 MAX_PER_ROUND 次，避免"一直错一直问"的卡死
   */
  _buildRound() {
    const ladder = WordStudyFlow.LADDER;
    const tasks = [];
    for (const st of this.stats.values()) {
      if (st.correct >= MASTERY_GOAL) continue;
      if ((st.roundAttempts || 0) >= MAX_ATTEMPTS_PER_WORD) continue;
      const retry = st.retrySteps?.shift();
      const idx = Math.min(this._round, ladder.length) - 1;
      tasks.push({ wordId: st.word.id, step: retry || ladder[idx], retry: !!retry });
    }
    this.queue = shuffle(tasks);
    this._round += 1;
    return tasks.length;
  }

  /** 使用提示（本题完成度降为「提示后完成」） */
  useHint() { this._hintUsed = true; }

  /** 「认识」卡片：点「我记住了」继续（不计分、不计入掌握次数） */
  ackMeet() {
    const task = this.current;
    if (!task || task.step !== 'meet') return null;
    this.queue.shift();
    const st = this.stats.get(task.wordId);
    st.seenSteps.add('meet');
    this._ensureWork();
    this.questionStartedAt = Date.now();
    return { ok: true };
  }

  /**
   * 提交检测答案
   * @returns {{correct:boolean, expected:string, detail:string, masteredNow:boolean, c:number, goal:number}}
   */
  /**
   * 提交一次作答。
   * @param {*} response
   * @param {object} [opts] { keep:true } —— 只判分不推进队列（用于"再试一次"原地重答）
   */
  submit(response, opts = {}) {
    const task = this.current;
    if (!task || task.step === 'meet') return null;
    const q = this.currentQuestion();
    const st = this.stats.get(task.wordId);
    const graded = grade(q, response);

    // 原地重试：只记一笔「这一次答错了」，掌握次数不加，队列不动，
    // 这样孩子点"再试一次"看到的还是同一道题，而不是被推到下一题。
    if (opts.keep) {
      if (graded.correct) {
        // 第二次答对：算一次"提示后完成"，掌握次数 +1（不算独立）
        st.correct += 1;
        this.streak += 1;
        this.records.push(this._record(q, task, response, graded, true));
        this.queue.shift();
        this._ensureWork();
      } else {
        st.wrong += 1;
        this.streak = 0;
        // 不写 record，避免同一题反复答错把成绩单撑爆；只累计错误次数
        this._retryMisses = (this._retryMisses || 0) + 1;
      }
      this.questionStartedAt = Date.now();
      this._hintUsed = false;
      return { ...graded, kept: true, c: st.correct, goal: MASTERY_GOAL, wordEn: st.word.en };
    }

    // 独立答对：没看提示且第一次就答对
    if (!graded.correct) {
      // 答错 → 不进错题本之前的这一笔留着（错题要能入账），但**队列不推进**，
      // 让学生可以点「再试一次」在同一个题上重答；「下一题」才真正前进。
      st.wrong += 1;
      this.streak = 0;
      this.records.push(this._record(q, task, response, graded, true));
      this.questionStartedAt = Date.now();
      this._hintUsed = false;
      return { ...graded, kept: true, c: st.correct, goal: MASTERY_GOAL, wordEn: st.word.en };
    }

    st.correct += 1;
    if (!this._hintUsed) st.independent += 1;
    this.streak += 1;
    st.attempts += 1;
    st.roundAttempts = (st.roundAttempts || 0) + 1;
    st.seenSteps.add(task.step);

    this.records.push(this._record(q, task, response, graded, !graded.correct));
    this.queue.shift();

    const masteredNow = graded.correct && st.correct === MASTERY_GOAL;

    this._ensureWork();

    this.questionStartedAt = Date.now();
    this._hintUsed = false;

    return {
      ...graded,
      masteredNow,
      c: st.correct,
      goal: MASTERY_GOAL,
      wordEn: st.word.en,
    };
  }

  /**
   * 队列空了就安排下一步工作。
   * 注意：必须**有上限**，否则「所有词都到了 3 次但队列因故为空」会死循环。
   * 规则：
   *   - 还有词没掌握 → 再来一轮（最多 12 轮，防止极端情况卡死）
   *   - 全部掌握且还没做过应用任务 → 插入「用一用」
   *   - 否则结束
   */
  _ensureWork() {
    if (this.queue.length) return;
    if (this.applying || this._applied) return;

    const alive = [...this.stats.values()].filter(st => (st.roundAttempts || 0) < MAX_ATTEMPTS_PER_WORD);
    const pending = alive.filter(st => st.correct < MASTERY_GOAL);

    // 还有词没掌握 → 排下一轮（轮次数量也有上限，双保险）
    if (pending.length && this._round <= 10) {
      this._buildRound();
      if (this.queue.length) return;
    }

    // 没人需要再练了 → 插入「用一用」小任务，做完就结束
    const anyCorrect = [...this.stats.values()].some(st => st.correct > 0);
    if (!this._applied && anyCorrect) {
      this._insertContextTask();
      this._applied = true;
    }
  }

  /** 整轮结束后的「单元词汇应用」小任务（很短，2~3 个空） */
  _insertContextTask() {
    const words = this.words.slice(0, 6);
    if (words.length < 2) return;
    const picked = sample(words, Math.min(3, words.length));
    const lines = picked.map(w => {
      const src = w.sentence || w.phrase;
      return src ? { w, src } : null;
    }).filter(Boolean);
    if (!lines.length) return;

    this.contextTask = lines.map(({ w, src }) => {
      const toks = tokenize(src);
      const idx = toks.findIndex(t => t.toLowerCase().replace(/[^a-z]/g, '') === w.en.toLowerCase().replace(/[^a-z]/g, ''));
      const blanks = idx >= 0 ? [idx] : [Math.max(0, toks.length - 2)];
      const answers = blanks.map(i => toks[i]);
      const shown = toks.map((t, i) => (blanks.includes(i) ? '____' : t)).join(' ')
        .replace(/\s+([.,!?;:])/g, '$1');
      return makeQuestion({
        id: `ctx-${w.id}`,
        textbookId: this.textbookId,
        unitId: this.unitId,
        type: 'fill',
        skill: 'W',
        level: 6,
        refType: 'word',
        refId: w.id,
        step: 'apply',
        origin: 'generated',
        status: 'confirmed',
        prompt: shown,
        audioUrl: '',
        audio: src,
        answer: answers,
        explain: `${src}\n${w.sentenceZh || w.phraseZh || w.zh}`,
        hint: `首字母 ${answers[0]?.[0] || w.en[0]}`,
      });
    });

    this.queue = this.contextTask.map((q, i) => ({ wordId: q.refId, step: 'apply', ctxIndex: i }));
    this.applying = true;
  }

  /** 当前的语境应用题（如果正处在应用环节） */
  currentApplyQuestion() {
    const task = this.current;
    if (!task || task.step !== 'apply') return null;
    return this.contextTask?.[task.ctxIndex] || null;
  }

  /** 提交语境应用题 */
  submitApply(response) {
    const task = this.current;
    const q = this.currentApplyQuestion();
    if (!task || !q) return null;
    const graded = grade(q, response);
    const rec = {
      index: this.records.length,
      questionId: q.id,
      type: q.type,
      skill: 'W',
      level: 6,
      refType: 'word',
      refId: q.refId,
      unitId: this.unitId,
      prompt: q.prompt,
      expected: graded.expected,
      response: Array.isArray(response) ? response.join('|') : String(response ?? ''),
      correct: !!graded.correct,
      partial: graded.partial ?? (graded.correct ? 1 : 0),
      hintUsed: this._hintUsed,
      skipped: false,
      completion: graded.correct ? (this._hintUsed ? 'hinted' : 'independent') : 'failed',
      step: 'apply',
      costMs: Date.now() - this.questionStartedAt,
      answeredAt: nowISO(),
    };
    this.records.push(rec);
    this.queue.shift();
    this.questionStartedAt = Date.now();
    this._hintUsed = false;
    return graded;
  }

  /** 构造一条作答记录（与 PracticeSession.records 同构，供 records.js 直接消费） */
  _record(q, task, response, graded, forceHinted) {
    const hinted = this._hintUsed || !!forceHinted;
    return {
      index: this.records.length,
      questionId: q.id,
      type: q.type,
      skill: 'W',
      level: q.level,
      refType: 'word',
      refId: task.wordId,
      unitId: this.unitId,
      prompt: q.prompt,
      expected: graded.expected,
      response: Array.isArray(response) ? response.join('|') : String(response ?? ''),
      correct: !!graded.correct,
      partial: graded.partial ?? (graded.correct ? 1 : 0),
      hintUsed: hinted,
      skipped: false,
      completion: graded.correct ? (hinted ? 'hinted' : 'independent') : 'failed',
      step: task.step,
      costMs: Date.now() - this.questionStartedAt,
      answeredAt: nowISO(),
    };
  }

  /** 已完成掌握的词 / 总词数 */
  masteryCount() {
    let done = 0;
    for (const st of this.stats.values()) if (st.correct >= MASTERY_GOAL) done += 1;
    return { done, total: this.words.length };
  }

  /** 每个词的掌握进度（用于顶部小圆点/结束页） */
  wordProgress() {
    return this.words.map(w => {
      const st = this.stats.get(w.id);
      return { word: w, correct: st.correct, goal: MASTERY_GOAL, mastered: st.correct >= MASTERY_GOAL, wrong: st.wrong };
    });
  }

  /** 转成 records / rewards 能吃的 summary（与 PracticeSession.summary 同构） */
  summary() {
    const total = this.records.length;
    const correct = this.records.filter(r => r.correct).length;
    const independent = this.records.filter(r => r.completion === 'independent').length;
    const hinted = this.records.filter(r => r.completion === 'hinted').length;
    const failed = this.records.filter(r => r.completion === 'failed').length;
    const byLevel = {};
    for (const r of this.records) {
      const k = String(r.level ?? 0);
      byLevel[k] = byLevel[k] || { total: 0, correct: 0 };
      byLevel[k].total += 1;
      if (r.correct) byLevel[k].correct += 1;
    }
    const m = this.masteryCount();
    return {
      id: this.id,
      module: 'word',
      title: this.title,
      unitId: this.unitId,
      textbookId: this.textbookId,
      total,
      correct,
      independent,
      hinted,
      failed,
      accuracy: total ? Math.round((correct / total) * 100) : 0,
      score: total ? Math.round((correct / total) * 100) : 0,
      bySkill: { W: { total, correct } },
      byLevel,
      startedAt: this.startedAt,
      endedAt: nowISO(),
      durationMs: Date.now() - new Date(this.startedAt).getTime(),
      records: this.records,
      // 额外信息（rewards 不认识也无害）
      wordsTotal: this.words.length,
      wordsMastered: m.done,
    };
  }
}
